/**
 * Created by Daniel Wang on 1/14/2024.
 * Description: admin shared page component
 * ------ maintenance history ------
 */

import { ColDef, GridApi, GridOptions } from 'ag-grid-community';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { AdminPagesService } from '../../../tamalelibs/services/admin-page.service';
import { AdminPageFeedback, AdminPageFeedbackTypes, AdminPageSetting, AGGridOptions } from '../admin-page-setting.model';
import { adminPagesConstants, adminSharedPageConstants } from '../admin-page-setting.constants';
import { ActivatedRoute } from '@angular/router';
import { SlideSheetService } from '../../../services/slide-sheet.service';
import { ToastService } from '../../../widgets/toast/toast.service';
import { NotificationStyles } from '../../../widgets/notification/notification.model';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../redux';
import { AdminPageActionTypes } from '../../../redux/actions/admin-page.actions';
import { lastCustomActionSelector } from '../../../redux/reducers/action-history.reducer';
import { AdminPageTypes, adminSharedPageSelector } from '../../../redux/reducers/admin-page.reducer';
import { SlideSheetActionTypes } from '../../slide-sheet/slide-sheet.model';
import { AlertWindowService } from '../../../widgets/alert-window/alert-window.service';
import { StringLiteralsPipe } from '../../../pipes/translate.pipe';
import { adminPageType } from '../../../tamalelibs/models/admin-page.model';
import { ActionHistoryActionTypes } from '../../../redux/actions/action-history.actions';
import { ButtonConfig, SplitButtonConfig } from '../../../widgets/split-button/split-button.model';
import { DialogAction, DialogService, DialogType } from '../../../services/dialog.service';

@Component({
    selector: 'tam-admin-shared-page',
    templateUrl: './admin-shared-page.component.html',
    styleUrls: ['./admin-shared-page.component.scss']
})
export class AdminSharedPageComponent implements OnInit, OnDestroy {
    // #region public properties
    btnTitleShared = '';
    columnDefs: Array<ColDef>;
    gridOptions: GridOptions;
    isButtonDisabled = false;
    isHiddenButton = false;
    isShowNotes = false;
    isWebsites = false;
    name = '';
    noDataStyle = 'none';
    notes = '';
    reloading = false;
    rowData: Array<any> = [];
    splitButtonConfig: SplitButtonConfig;
    // #endregion

    // #region private properties
    private _destroySubscriptions: Array<Subscription> = [];
    private _gridApi: GridApi;
    private _isCreateStreetEvent = false;
    private _pageType = '';
    private _sharedPage: AdminPageSetting;
    // #endregion

    // #region constructor
    constructor(
        private _alertWindow: AlertWindowService,
        private _adminPagesServcie: AdminPagesService,
        private _dialogService: DialogService,
        private _route: ActivatedRoute,
        private _slideSheetService: SlideSheetService,
        private _store: Store<AppState>,
        private _toast: ToastService,
    ) {
    }
    // #endregion

    // #region angular functions
    ngOnInit() {
        // get page type by url
        this._pageType = this._route.snapshot.data['page'];
        this._sharedPage = adminPagesConstants[this._pageType];
        this.name = adminSharedPageConstants[this._pageType].name;
        this.notes = adminSharedPageConstants[this._pageType]?.notes ? adminSharedPageConstants[this._pageType]?.notes : '';
        if (this.notes) {
            this.isShowNotes = true;
        }
        this._destroySubscriptions.push(
            this._sharedPage.feedbackSubject$.subscribe(feedback => this._handleGridFeedback(feedback)),
            this._store.pipe(
                select(lastCustomActionSelector)
            ).subscribe(action => {
                this._onSubscribeActionMessage(action);
            }),
            this._store.pipe(
                select(adminSharedPageSelector)
            ).subscribe(data => {
                if (data) {
                    if (Array.isArray(data)) {
                        this.rowData = data;
                    } else {
                        if (JSON.stringify(data) === '{}') {
                            this.rowData = [];
                        } else {
                            this.rowData = [data];
                        }
                    }
                    this._handleStreetEventConfig(this.rowData);
                    this.reloading = false;
                }
            })
        );
        this._initPage();
        this._initAGGridData();
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }
    // #endregion

    // #region public functions
    onGridReady(params) {
        this._gridApi = params.api;
        setTimeout(() => {
            this._gridApi.sizeColumnsToFit();
        });
    }

    onClick(event) {
        if (this._pageType === AdminPageTypes.STREET_EVENTS_CONFIGURATION) {
            if (this._isCreateStreetEvent) {
                this._slideSheetService.slideSheetActionSubject$.next({
                    type: adminSharedPageConstants[this._pageType].slideSheet
                });
            } else {
                this.isButtonDisabled = true;
                this._adminPagesServcie.runStreetEventAdapter().pipe(
                    take(1),
                    catchError((error) => {
                        this.isButtonDisabled = false;
                        this._toast.notify({
                            style: NotificationStyles.Error,
                            message: error
                        });
                        return of(null);
                    })
                ).subscribe(res => {
                    if (res) {
                        this.isButtonDisabled = false;
                        this._toast.notify({
                            style: NotificationStyles.Success,
                            message: 'Ran the Street Events adapter at ' + new Date()
                        });
                    }
                });
            }
        } else {
            this._slideSheetService.slideSheetActionSubject$.next({
                type: adminSharedPageConstants[this._pageType].slideSheet
            });
        }
    }
    // #endregion

    // #region private functions
    private _closeSlideSheet() {
        this._slideSheetService.slideSheetActionSubject$.next({
            type: SlideSheetActionTypes.CLOSE
        });
    }

    private _handleButtonClick(feedback: AdminPageFeedback) {
        const scheduledJobId = feedback.payload?.data.id;
        let jobName = feedback.payload?.data.name;
        jobName = jobName.replace(/\*/, '');
        this._adminPagesServcie.handleRun(this._pageType, scheduledJobId).pipe(
            take(1),
            catchError((res) => {
                this._toast.notify({
                    style: NotificationStyles.Success,
                    message: 'Run job ' + jobName + ' failed.'
                });
                return of(null);
            })
        ).subscribe(res => {
            this._toast.notify({
                style: NotificationStyles.Success,
                message: 'Run job ' + jobName + ' successfully.'
            });
        });
    }

    private _handleDelete(feedback: AdminPageFeedback) {
        const item = feedback?.payload?.data;
        let message = `Are you sure you want to delete ${item.name ? item.name : item.username}?`;
        if (adminSharedPageConstants[this._pageType].type === adminPageType.tokenManagement) {
            message = 'Token will be deleted and the user of this token will be logout. Continue?';
            item['name'] = 'Token';
        } else if (adminSharedPageConstants[this._pageType].type === adminPageType.streetEvents) {
            message = `Are you sure you want to delete Street Events adapter config?`;
        }
        const subscription = this._alertWindow.warn(StringLiteralsPipe.translate('general.delete_confirmation_required'),
            [message], 'Delete', 'Cancel')
            .subscribe((result: boolean) => {
                if (result) {
                    this._store.dispatch({
                        type: AdminPageActionTypes.DELETE_SHARED_PAGE,
                        payload: {
                            type: adminSharedPageConstants[this._pageType].type,
                            id: item.id ? item.id : item.ID,
                            name: item.name ? item.name : item.username
                        }
                    });
                }
                subscription.unsubscribe();
            });
    }

    private _handleEdit(feedback: AdminPageFeedback) {
        this._slideSheetService.slideSheetActionSubject$.next({
            type: adminSharedPageConstants[this._pageType].slideSheet,
            payload: feedback.payload.data
        });
    }

    private _handleGridFeedback(feedback: AdminPageFeedback) {
        switch (feedback.type) {
            case AdminPageFeedbackTypes.EDIT:
                this._handleEdit(feedback);
                break;
            case AdminPageFeedbackTypes.DELETE:
                this._handleDelete(feedback);
                break;
            case AdminPageFeedbackTypes.RUN:
                this._handleButtonClick(feedback);
                break;
            default:
                break;
        }
    }

    private _handleStreetEventConfig(data) {
        if (this._pageType === AdminPageTypes.STREET_EVENTS_CONFIGURATION) {
            if (data.length === 0) {
                this.btnTitleShared = StringLiteralsPipe.translate('adminConsole.create_street_events_config');
                this._isCreateStreetEvent = true;
            }
        }
    }

    private _initAGGridData() {
        this.reloading = true;
        this._store.dispatch({
            type: AdminPageActionTypes.GET_SHARED_PAGE,
            payload: { type: adminSharedPageConstants[this._pageType].type }
        });
    }

    private _initPage() {
        this.btnTitleShared = adminSharedPageConstants[this._pageType].btnTitle;
        this.isHiddenButton = adminSharedPageConstants[this._pageType]?.isHiddenButton;
        if (this._pageType === AdminPageTypes.WEB_SITES) {
            this.isWebsites = true;
            this.splitButtonConfig = {
                primaryAction: {
                    displayText: adminSharedPageConstants[this._pageType].btnTitle,
                    onClick: () => {
                        this.onClick(event);
                    }
                } as ButtonConfig,
                secondaryActions: [
                    {
                        displayText: StringLiteralsPipe.translate('adminConsole.add_sites_from_file'),
                        onClick: () => {
                            const dialogAction = new DialogAction(DialogType.WebSitesDialog);
                            this._dialogService.dialogOpen$.next(dialogAction);
                        },
                        isDisabled: true
                    } as ButtonConfig,
                ],
                feedbackSubject$: new Subject<any>()
            } as SplitButtonConfig;
        } else {
            this.isWebsites = false;
        }
    }

    private _removeTriggeredItemInActionHistory(actionType) {
        this._store.dispatch({
            type: ActionHistoryActionTypes.REMOVE_ITEM,
            payload: {
                type: actionType
            }
        });
    }

    /**
     * Defining data for columns
     */
    private _setAgGridColumns() {
        if (this.rowData && this.rowData[0]) {
            this.columnDefs = this._adminPagesServcie.dynamicallySetColumnDef(this.rowData[0], adminSharedPageConstants[this._pageType]?.columnHeader);
            this.columnDefs.push(...adminSharedPageConstants[this._pageType].extraColumn);
        }
    }

    private _onSubscribeActionMessage(action) {
        if (action.type === AdminPageActionTypes.GET_SHARED_PAGE_SUCCESS) {
            this.rowData = action.payload;
            this._handleStreetEventConfig(this.rowData);
            this._setAgGridColumns();
            this.gridOptions = AGGridOptions;
            if (adminSharedPageConstants[this._pageType]?.isShowRowGroupPanel) {
                this.gridOptions.rowGroupPanelShow = 'always';
            } else {
                this.gridOptions.rowGroupPanelShow = 'never';
            }
        } else if (action.type === AdminPageActionTypes.DELETE_SHARED_PAGE_SUCCESS) {
            let messageInfo = (action.payload.name ? action.payload.name : action.payload.username) + ' successfully deleted.';
            if (this._pageType === AdminPageTypes.STREET_EVENTS_CONFIGURATION) {
                messageInfo = StringLiteralsPipe.translate('adminConsole.street_events_adapter_config_deleted');
            }
            this._toast.notify({
                style: NotificationStyles.Success,
                message: messageInfo
            });
            this._removeTriggeredItemInActionHistory(action.type);
        } else if (action.type === AdminPageActionTypes.CREATE_SHARED_PAGE_SUCCESS) {
            let messageInfo = (action.payload.data.name ? action.payload.data.name : action.payload.data.username) + ' successfully created.';
            if (this._pageType === AdminPageTypes.STREET_EVENTS_CONFIGURATION) {
                this.btnTitleShared = adminSharedPageConstants[this._pageType].btnTitle;
                this._isCreateStreetEvent = false;
                messageInfo = StringLiteralsPipe.translate('adminConsole.street_events_adapter_config_created');
            }
            this._toast.notify({
                style: NotificationStyles.Success,
                message: messageInfo
            });
            this._closeSlideSheet();
            this._removeTriggeredItemInActionHistory(action.type);
        } else if (action.type === AdminPageActionTypes.EDIT_SHARED_PAGE_SUCCESS) {
            let messageInfo = (action.payload.name ? action.payload.name : action.payload.username) + ' successfully updated.';
            if (this._pageType === AdminPageTypes.STREET_EVENTS_CONFIGURATION) {
                messageInfo = StringLiteralsPipe.translate('adminConsole.street_events_adapter_config_updated');
            }
            this._toast.notify({
                style: NotificationStyles.Success,
                message: messageInfo
            });
            this._closeSlideSheet();
            this._removeTriggeredItemInActionHistory(action.type);
        } else if (action.type === AdminPageActionTypes.DELETE_SHARED_PAGE_FAILURE) {
            this._toast.notify({
                style: NotificationStyles.Error,
                message: action.payload
            });
            this._removeTriggeredItemInActionHistory(action.type);
        }
    }
    // #endregion
}
